import type { Effect } from '@flobi/artwork-core';
import { ADJUST_SHADER_ID } from '../imageTools/adjust';

/**
 * What a layer's effects are, away from the panels that draw them. Apart from Effects.tsx on
 * purpose: the canvas reads MOST_EFFECTS, and while that lived beside the components, saving
 * one of them re-ran the hook that owns the canvas and took the open editor with it (1 Nov).
 */

/** The most effects one layer's list may hold: the engine's EffectListSchema refuses more. */
export const MOST_EFFECTS = 32;

/** What a layer's tiles depend on: the layer as stored, so they are drawn again only when it changes. */
export function previewKeyOf(node: unknown): string {
  try { return JSON.stringify(node) ?? ''; } catch { return ''; }
}

/** What a layer is carrying, read the same way wherever it is asked for. */
export function effectsOf(node: unknown): readonly Effect[] {
  return (((node as { effects?: Effect[] }).effects ?? []) as Effect[]).filter(Boolean);
}

/**
 * The effects a layer's Effects list shows, and where each sits in the layer's own list (`at`),
 * for the list's edits. What the picture's Edit tab sets is left out (Tarek, 15 Oct: "no need to add
 * this as an effect, just keep it in the Edit tab" — then "hide blur from effects too"): Adjust on
 * anything, and the blur on a picture (a shape or text keeps its blur here; it has no Edit tab).
 * Both are still drawn, exported and kept in the layer's effects.
 */
export function listedEffects(node: unknown): { effects: Effect[]; at: number[] } {
  const all = effectsOf(node), effects: Effect[] = [], at: number[] = [];
  const record = node as { type?: string; fills?: { type: string; visible?: boolean }[] } | undefined;
  const picture = record?.type === 'image' || !!record?.fills?.some(paint => paint.type === 'image' && paint.visible !== false);
  all.forEach((effect, index) => {
    if (effect.type === 'shader' && effect.shaderId === ADJUST_SHADER_ID) return;
    if (picture && effect.type === 'layer-blur') return;
    effects.push(effect); at.push(index);
  });
  return { effects, at };
}
